import {
  Autocomplete,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * A controlled select field component without react-hook-form dependencies.
 * Supports single or multiple selection, disabling options, and displays error/helper text.
 */
export interface ControlledSelectFieldProps {
  /**
   * The name attribute for the select element.
   */
  name?: string;
  /**
   * The current selected value(s).
   */
  value: string | string[];
  /**
   * Callback invoked when the selected value changes.
   */
  onChange: (value: string | string[]) => void;
  /**
   * Options to display. Can be an array of strings or objects with label/value.
   */
  options: Array<string | { label: string; value: string }>;
  /**
   * Whether multiple selection is allowed.
   */
  multiple?: boolean;
  /**
   * Array of option values that should be disabled.
   */
  disabledOptions?: string[];
  /**
   * Label to display above the select.
   */
  label?: string;
  /**
   * Whether the select should open its options when it first renders.
   */
  defaultOpen?: boolean;
  /**
   * Whether to keep the select mounted but visually hidden.
   */
  hideControl?: boolean;
  /**
   * Callback invoked when the select options menu closes.
   */
  onClose?: () => void;
  /**
   * If true, the select will be styled as having an error.
   */
  error?: boolean;
  /**
   * Helper text to show beneath the select (e.g. validation message).
   */
  helperText?: string;
  /**
   * When true, renders an Autocomplete with typeahead filtering on the option
   * label (case-insensitive, also matching the value) instead of a plain
   * Select. Multiple selections render as chips inside the field. Defaults to
   * false.
   */
  searchable?: boolean;
}

type NormalizedOption = { label: string; value: string };

export function ControlledSelectField({
  name,
  value,
  onChange,
  options,
  multiple = false,
  disabledOptions = [],
  label,
  defaultOpen = false,
  hideControl = false,
  onClose,
  error = false,
  helperText,
  searchable = false,
}: Readonly<ControlledSelectFieldProps>) {
  const theme = useTheme();
  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const newValue = event.target.value;
    onChange(newValue);
  };

  const borderColor = error
    ? theme.palette.error.main
    : theme.palette.text.primary;

  if (searchable) {
    const normalized: NormalizedOption[] = (options ?? []).map((opt) =>
      typeof opt === "string" ? { label: opt, value: opt } : opt
    );
    const byValue = new Map(normalized.map((o) => [o.value, o]));
    const toOption = (v: string): NormalizedOption =>
      byValue.get(v) ?? { label: v, value: v };
    const autocompleteProps = {
      options: normalized,
      fullWidth: true,
      onClose,
      getOptionLabel: (o: NormalizedOption) => o.label,
      isOptionEqualToValue: (a: NormalizedOption, b: NormalizedOption) =>
        a.value === b.value,
      getOptionDisabled: (o: NormalizedOption) =>
        disabledOptions.includes(o.value),
      filterOptions: (
        opts: NormalizedOption[],
        state: { inputValue: string }
      ) => {
        const q = state.inputValue.trim().toLowerCase();
        return q
          ? opts.filter(
              (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q)
            )
          : opts;
      },
      renderInput: (params: React.ComponentProps<typeof TextField>) => (
        <TextField {...params} name={name} error={error} />
      ),
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "6px",
          fontSize: "14px",
          minHeight: "45px",
        },
        "& input": { fontSize: "14px" },
        fieldset: { borderRadius: "6px", borderColor },
      },
    };

    return (
      <FormControl
        fullWidth
        error={error}
        sx={hideControl ? { visibility: "hidden" } : undefined}
      >
        {label && (
          <Typography
            variant="body2"
            sx={{ marginBottom: "4px", fontWeight: 500 }}
          >
            {label}
          </Typography>
        )}
        {multiple ? (
          <Autocomplete
            {...autocompleteProps}
            multiple
            disableCloseOnSelect
            value={(Array.isArray(value) ? value : []).map(toOption)}
            onChange={(_, next) => onChange(next.map((o) => o.value))}
          />
        ) : (
          <Autocomplete
            {...autocompleteProps}
            value={
              typeof value === "string" && value !== "" ? toOption(value) : null
            }
            onChange={(_, next) => onChange(next ? next.value : "")}
          />
        )}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      error={error}
      sx={hideControl ? { visibility: "hidden" } : undefined}
    >
      {label && (
        <Typography
          variant="body2"
          sx={{ marginBottom: "4px", fontWeight: 500 }}
        >
          {label}
        </Typography>
      )}
      <Select
        labelId={label ? `${name}-label` : undefined}
        name={name}
        value={value}
        onChange={handleChange}
        onClose={onClose}
        defaultOpen={defaultOpen}
        multiple={multiple}
        displayEmpty
        fullWidth
        MenuProps={
          hideControl ? { sx: { transform: "translateY(-65px)" } } : undefined
        }
        slotProps={{
          input: {
            style: {
              borderRadius: "6px",
              height: "45px",
            },
          },
        }}
        sx={{
          "& input": {
            fontSize: "14px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "14px",
          },
          fontSize: "14px",
          height: "45px",
          fieldset: {
            borderRadius: "6px",
            borderColor: error
              ? "#d32f2f !important"
              : theme.palette.text.primary,
          },
        }}
      >
        {options?.map((opt) => {
          const val = typeof opt === "string" ? opt : opt.value;
          const lab = typeof opt === "string" ? opt : opt.label;
          return (
            <MenuItem
              key={val}
              value={val}
              disabled={disabledOptions.includes(val)}
              sx={{ fontSize: "14px" }}
            >
              {lab}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
